import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ChevronDown, Home, Sparkles, Building2, BarChart3, Check, Star } from "lucide-react";

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [expandedService, setExpandedService] = useState(null);

  const services = [
    {
      id: 1,
      icon: Home,
      title: "Owner Property Concierge",
      description: "Personalized property management for absentee or seasonal homeowners. Includes regular property checks, seasonal maintenance coordination, emergency response, and peace-of-mind updates.",
      image: "https://i.pinimg.com/736x/95/0f/ad/950fadf73b18c7f544cedbb5db105bba.jpg",
      pricing: [
        {
          tier: "Essential",
          price: "$850",
          period: "/ month",
          popular: false,
          features: ["Bi-monthly property inspections", "Seasonal preparation coordination", "Emergency alerts", "Monthly property updates"]
        },
        {
          tier: "Premium",
          price: "$1,450",
          period: "/ month",
          popular: true,
          features: ["Everything in Essential plus:", "Weekly property inspections", "Vendor coordination", "Priority support", "Maintenance scheduling assistance"]
        },
        {
          tier: "Private Estate Care",
          price: "Custom",
          period: " Pricing",
          popular: false,
          features: ["For larger homes or owners requiring more frequent oversight and concierge support"]
        }
      ]
    },
    {
      id: 2,
      icon: Sparkles,
      title: "Luxury Arrival & Lifestyle Concierge",
      description: "White-glove guest and owner experience. Matthew ensures homes are fully prepared for arrival, including guest check-ins, home setup, and personalized luxury touches.",
      image: "https://i.pinimg.com/736x/27/79/3a/27793a2a7bfb1577e465a73a34a2aae4.jpg",
      pricing: [
        {
          tier: "Standard",
          price: "$350",
          period: "/ arrival",
          popular: false,
          features: ["Home preparation before arrival", "Climate and lighting setup", "Basic guest welcome preparation"]
        },
        {
          tier: "Premium",
          price: "$550",
          period: "/ arrival",
          popular: true,
          features: ["Full arrival preparation", "Guest check-in coordination", "Home staging and setup"]
        },
        {
          tier: "Elite",
          price: "$750",
          period: "/ arrival",
          popular: false,
          features: ["Premium package", "Lifestyle request coordination", "In-home concierge support"]
        }
      ]
    },
    {
      id: 3,
      icon: Building2,
      title: "Spec Home / Builder Liaison",
      description: "Expert coordination for luxury builds or renovations. Matthew liaises with builders, contractors, and designers to ensure every project meets the highest standards.",
      image: "https://i.pinimg.com/736x/9d/72/05/9d7205f0e876603db114ae7f87a928ee.jpg",
      pricing: [
        {
          tier: "Project Management",
          price: "$95",
          period: "/ hour",
          popular: false,
          features: ["Contractor coordination", "Vendor management", "Progress updates"]
        },
        {
          tier: "Full Oversight",
          price: "Starting at $2,500",
          period: "/ project",
          popular: true,
          features: ["End-to-end project coordination", "Quality assurance oversight", "Regular progress reporting"]
        }
      ]
    },
    {
      id: 4,
      icon: BarChart3,
      title: "Short-Term Rental Optimization",
      description: "Maximize rental income with minimal effort. Includes AI-assisted scheduling, guest management, and property care to ensure high occupancy and excellent reviews.",
      image: "https://i.pinimg.com/736x/19/ed/85/19ed8546424a1b12c9c2e485adf7fc36.jpg",
      pricing: [
        {
          tier: "Basic Optimization",
          price: "$350",
          period: "/ month",
          popular: false,
          features: ["Scheduling support", "Guest communication assistance", "Performance updates"]
        },
        {
          tier: "Premium Optimization",
          price: "$650",
          period: "/ month",
          popular: true,
          features: ["Everything in Basic plus:", "Guest arrival coordination", "Property preparation for stays", "Full guest management support"]
        }
      ]
    }
  ];

  const toggleService = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-24 md:py-32 bg-gray-50"
      data-testid="services-section"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-accent text-sm font-semibold tracking-wider uppercase">
            Core Services
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mt-4 mb-6 leading-tight">
            Comprehensive Property <span className="italic">Solutions</span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg">
            Tailored packages to meet every property owner's needs, from routine care to premium lifestyle services.
          </p>
        </motion.div>

        {/* Services - Accordion Style like reference */}
        <div className="space-y-4">
          {services.map((service, index) => {
            const isExpanded = expandedService === service.id;
            const IconComponent = service.icon;
            
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                data-testid={`service-card-${index}`}
              >
                {/* Service Header - Clickable */}
                <button
                  onClick={() => toggleService(service.id)}
                  className="w-full p-6 flex items-center gap-6 text-left hover:bg-gray-50/50 transition-colors"
                  data-testid={`service-toggle-${service.id}`}
                >
                  {/* Small Image */}
                  <div className="w-24 h-24 md:w-28 md:h-28 rounded-lg overflow-hidden flex-shrink-0">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Icon + Title + Description */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <IconComponent className="w-5 h-5 text-navy/60" strokeWidth={1.5} />
                      <h3 className="font-heading text-xl md:text-2xl text-navy">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed line-clamp-2">
                      {service.description}
                    </p>
                  </div>

                  {/* Chevron */}
                  <ChevronDown 
                    className={`w-6 h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
                  />
                </button>

                {/* Expandable Pricing Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                        <p className="text-sm text-gray-500 mb-4">Pricing Options:</p>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {service.pricing.map((tier) => (
                            <div 
                              key={tier.tier}
                              className={`p-4 rounded-xl border ${
                                tier.popular 
                                  ? "border-navy bg-navy/5" 
                                  : "border-gray-200 bg-gray-50"
                              }`}
                            >
                              {tier.popular && (
                                <span className="flex items-center gap-1 text-xs font-semibold text-navy mb-2">
                                  <Star className="w-3 h-3 fill-navy" />
                                  Most Popular
                                </span>
                              )}
                              <h4 className="font-semibold text-navy">{tier.tier}</h4>
                              <p className="font-bold text-navy text-lg mt-1">
                                {tier.price}<span className="text-gray-500 text-sm font-normal">{tier.period}</span>
                              </p>
                              <ul className="mt-3 space-y-1">
                                {tier.features.map((feature) => (
                                  <li key={feature} className="flex items-start gap-2 text-xs text-gray-600">
                                    <Check className="w-3 h-3 text-accent flex-shrink-0 mt-0.5" />
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Pricing Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-12 p-6 bg-white rounded-xl border border-gray-200 text-center"
        >
          <p className="text-gray-500 text-sm italic">
            Prices listed are starting estimates and may vary depending on property size, service scope, and specific homeowner needs. Final pricing will be confirmed after consultation.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
